import {
  PALLET_HEIGHT_IN,
  PALLET_WIDTH_IN,
} from "../../game/bench-work/pallet-geometry";
import { productBlueprintFor } from "../../game/bench-work/blueprint";
import {
  Board,
  CUTTING_BOARD_FOOTPRINTS,
  MaterialInstance,
  Panel,
  panelWidth,
} from "../../game/Materials";
import { PIXELS_PER_INCH } from "../shop-scale";
import { boardOutline } from "./board";
import { ContactShadow } from "./contactShadow";
import { rectOutline, scaleOutline, ShadowOutline } from "./shadowOutline";

/**
 * The one shadow a piece throws — every material's, in one place. A
 * piece casts exactly one shadow no matter how many boards it's built
 * from: a pallet or an assembled product shades its whole footprint
 * once, not a stack of per-part halos. `createMaterialSprite` draws it
 * on its own layer under the art, so the bench's hover rim can dress
 * the wood without tracing the shadow, and carrying a piece can spread
 * the shadow to say how far off the surface it rides.
 *
 * The shadow's outline is the piece's own: a lying board shades the
 * very polygon its face is painted in — wavy where an edge is still
 * sawn, slanted where an end is mitered (boardOutline) — and the rest
 * shade the box their art fills. The spread comes from how far the
 * piece stands off the surface (see contactShadow.ts): a lying board
 * its thickness, a standing board its width, a pallet its
 * stringer-and-decks height.
 */

export interface ShadowLook {
  /** The footprint's outline, centered, in pixels. */
  readonly outline: ShadowOutline;
  /** The outline's box — what a caller stretching the outline to
   * another footprint (the flip tumble) scales from. */
  readonly widthPx: number;
  readonly heightPx: number;
  readonly standInches: number;
}

/** The shadow a material would throw as placed — for callers that
 * blend between two placements (the flip tumble) instead of drawing
 * one outright. */
export function materialShadowLook(
  material: MaterialInstance,
  placement: { onEdge?: boolean; onEnd?: boolean } = {},
): ShadowLook | null {
  return shadowLook(material, placement);
}

export function drawMaterialShadow(
  shadow: ContactShadow,
  material: MaterialInstance,
  placement: { onEdge?: boolean; onEnd?: boolean } = {},
  carry: {
    /** Inches the hand holds the piece off the surface. */
    liftInches?: number;
    /** The art's own carry growth, so the pool tracks the lifted
     * wood's footprint and the penumbra stays even around it. */
    footprintScale?: number;
  } = {},
): void {
  const look = shadowLook(material, placement);
  if (!look) {
    shadow.clear();
    return;
  }
  const scale = carry.footprintScale ?? 1;
  const outline =
    scale === 1 ? look.outline : scaleOutline(look.outline, scale, scale);
  shadow.draw(outline, look.standInches, { liftInches: carry.liftInches });
}

/** A look whose outline is the plain box of its footprint. */
function boxLook(
  widthPx: number,
  heightPx: number,
  standInches: number,
): ShadowLook {
  return {
    outline: rectOutline(-widthPx / 2, -heightPx / 2, widthPx, heightPx),
    widthPx,
    heightPx,
    standInches,
  };
}

function shadowLook(
  material: MaterialInstance,
  placement: { onEdge?: boolean; onEnd?: boolean },
): ShadowLook | null {
  switch (material.type) {
    case "board": {
      const b = material as Board;
      if (placement.onEnd) {
        // The tallest a board can stand — the shadow spreads to its cap
        return boxLook(
          b.width * PIXELS_PER_INCH,
          (b.thickness / 4) * PIXELS_PER_INCH,
          b.length,
        );
      }
      if (placement.onEdge) {
        // A standing board stands its whole width off the bench, and
        // the shadow says so — wider than the same board's lying rim
        return boxLook(
          (b.thickness / 4) * PIXELS_PER_INCH,
          b.length * PIXELS_PER_INCH,
          b.width,
        );
      }
      // Lying, the board shades the polygon its face is drawn in — the
      // same seed the sprite draws with, so the two agree to the pixel
      return {
        outline: boardOutline(b, b.id),
        widthPx: b.width * PIXELS_PER_INCH,
        heightPx: b.length * PIXELS_PER_INCH,
        standInches: b.thickness / 4,
      };
    }

    case "plywood":
      return boxLook(
        material.width * PIXELS_PER_INCH,
        material.length * PIXELS_PER_INCH,
        material.thickness / 4,
      );

    case "panel":
      return boxLook(
        panelWidth(material as Panel) * PIXELS_PER_INCH,
        material.length * PIXELS_PER_INCH,
        material.thickness / 4,
      );

    case "endGrainSlice":
      // The slice as drawn: the strip run across, the 2" slice down
      // (drawEndGrainSlice)
      return boxLook(
        material.strips.reduce((sum, strip) => sum + strip.width, 0) *
          PIXELS_PER_INCH,
        2 * PIXELS_PER_INCH,
        material.thickness / 4,
      );

    case "pallet":
      // Two decks over their stringers — the whole sandwich stands off
      // the surface
      return boxLook(
        PALLET_WIDTH_IN * PIXELS_PER_INCH,
        PALLET_HEIGHT_IN * PIXELS_PER_INCH,
        4.5,
      );

    case "simpleCuttingBoard":
    case "stripedCuttingBoard":
    case "sunriseCuttingBoard":
    case "endGrainCuttingBoard":
    case "checkerboardCuttingBoard": {
      // An inch of cutting board off the bench — the penumbra rounds
      // the corners on its own
      const footprint = CUTTING_BOARD_FOOTPRINTS[material.type];
      return boxLook(
        footprint.widthIn * PIXELS_PER_INCH,
        footprint.heightIn * PIXELS_PER_INCH,
        1,
      );
    }

    case "tool":
      return null;

    default: {
      // A blueprint-assembled product shades its blueprint's box — the
      // very frame assembledProduct.ts draws in
      const blueprint = productBlueprintFor(material.type);
      if (!blueprint) return null;
      return boxLook(
        blueprint.widthIn * PIXELS_PER_INCH,
        blueprint.heightIn * PIXELS_PER_INCH,
        2,
      );
    }
  }
}
